// The notes a chat beside a workspace pins on a trace's cells
// (workspace_point), kept by the trace they annotate: a session's notes show
// on its card on any workspace, whichever chat is open, and nowhere else.
// Kept in this browser for the signed-in reader, each session's under a key
// of its own and read when that session is on a board; a chat's notes are
// also put back from its transcript whenever the chat loads
// (lib/chatWorkspaceNotes.js), so they reach any browser the chat does. The
// trace reading's own chat keeps its notes with its thread, beside it
// (lib/traceChatThreads.js).
//
// A note: { id, session, messageId?, printed?, lane?, text, chat?: { key,
// title }, n?, covers?, at }. `printed` is the turn number the board prints
// (its place in the whole record); `lane` a lane's id or label; `n` a
// walkthrough's moment's number in it, and `covers` the chat's claims and the
// case's notes that moment settles (cleanCovers).
import { useMemo, useRef, useSyncExternalStore } from "react";
import { onAuthIdentityChange } from "./authTokenStore.js";
import { browserStorageKey } from "./browserStorageScope.js";

const PREFIX = "origin-trace-notes-v1";
// Notes one session keeps, its newest: they are for the turns answers point
// at, and more would bury the card.
export const NOTE_CAP = 120;
// Sessions kept, the most recently written.
export const KEPT_SESSIONS = 200;
// How long the store waits for the signed-in reader to be known after a
// reload, checking each second.
const OWNER_WAIT_TRIES = 30;

const NONE = Object.freeze([]);
const EMPTY = Object.freeze({ notes: NONE, at: 0 });
const lower = (id) => String(id || "").trim().toLowerCase();
const ownerPrefix = () => browserStorageKey(PREFIX);

// What a note says of its moment, where its writer said (Investigate's do):
// worth a person's attention, safe to let go, missed by the review and its
// check, or a fact the check got wrong. A note without one is plain.
export const NOTE_KINDS = ["matters", "doesnt", "missed", "corrects"];

// What a walkthrough's moment covers: [{ kind: "claim", id, n? } | { kind:
// "case", id, n?, text }], the claims by the chat's ids and the case's notes
// with their words.
const COVERS_MAX = 8;
export function cleanCovers(list) {
  return (Array.isArray(list) ? list : [])
    .filter((c) => (c?.kind === "claim" || c?.kind === "case") && c.id != null && String(c.id).trim())
    .slice(0, COVERS_MAX)
    .map((c) => ({
      kind: c.kind,
      id: String(c.id),
      ...(Number.isInteger(c.n) && c.n > 0 ? { n: c.n } : {}),
      ...(c.kind === "case" && typeof c.text === "string" && c.text.trim() ? { text: c.text.trim().slice(0, 240) } : {}),
    }));
}

function cleanNote(n, session) {
  if (!n || typeof n !== "object" || !n.id || typeof n.text !== "string" || !n.text.trim()) return null;
  const covers = cleanCovers(n.covers);
  return {
    id: String(n.id),
    session,
    messageId: n.messageId ? String(n.messageId) : null,
    printed: Number.isInteger(n.printed) ? n.printed : null,
    lane: n.lane != null && String(n.lane).trim() ? String(n.lane) : null,
    text: n.text.trim(),
    chat: n.chat?.key ? { key: String(n.chat.key), title: String(n.chat.title || "") } : null,
    ...(NOTE_KINDS.includes(n.kind) ? { kind: n.kind } : {}),
    // A walkthrough's moment's place in it, where it runs across sessions.
    ...(Number.isInteger(n.n) && n.n > 0 ? { n: n.n } : {}),
    ...(covers.length ? { covers } : {}),
    at: Number(n.at) || 0,
  };
}

function cleanEntry(raw, session) {
  const notes = (Array.isArray(raw?.notes) ? raw.notes : []).map((n) => cleanNote(n, session)).filter(Boolean).slice(-NOTE_CAP);
  return { notes: notes.length ? notes : NONE, at: Number(raw?.at) || 0 };
}

// What each session holds, read once per reader: `${owner}|${session}`.
const held = new Map();
const subscribers = new Set();
let version = 0;

function emit() {
  version += 1;
  for (const fn of subscribers) fn();
}

// Right after a reload the reader is not known yet, and neither is where
// their notes are kept: look again each second, and draw again once it is.
let waiting = null;
function waitForOwner() {
  if (waiting || typeof setTimeout !== "function") return;
  let tries = 0;
  const look = () => {
    tries += 1;
    if (ownerPrefix()) {
      waiting = null;
      emit();
    } else if (tries < OWNER_WAIT_TRIES) {
      waiting = setTimeout(look, 1000);
    } else {
      waiting = null;
    }
  };
  waiting = setTimeout(look, 1000);
}

function entryOf(session, owner = ownerPrefix()) {
  const k = `${owner ?? ""}|${session}`;
  const had = held.get(k);
  if (had) return had;
  let entry = EMPTY;
  if (owner) {
    try {
      const raw = globalThis.localStorage?.getItem(`${owner}:${session}`);
      if (raw) entry = cleanEntry(JSON.parse(raw), session);
    } catch { /* unreadable: it starts empty */ }
  } else {
    waitForOwner();
  }
  held.set(k, entry);
  return entry;
}

// The sessions this reader keeps notes for, oldest written first.
function keptSessions(storage, owner) {
  const out = [];
  try {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key || !key.startsWith(`${owner}:`)) continue;
      let at = 0;
      try { at = Number(JSON.parse(storage.getItem(key) || "null")?.at) || 0; } catch { at = 0; }
      out.push({ key, at });
    }
  } catch { /* a storage without key(): nothing to make room from */ }
  return out.sort((a, b) => a.at - b.at);
}

// Kept for the session, making room when it is a new one past the cap, or
// when the browser refuses the write (its quota is shared with everything
// else on the origin): the sessions written longest ago go first.
function persist(owner, session, entry) {
  const storage = globalThis.localStorage;
  if (!storage) return;
  const key = `${owner}:${session}`;
  const value = JSON.stringify(entry);
  const others = () => keptSessions(storage, owner).filter((s) => s.key !== key);
  if (storage.getItem(key) == null) {
    const old = others();
    for (const s of old.slice(0, Math.max(0, old.length - (KEPT_SESSIONS - 1)))) storage.removeItem(s.key);
  }
  for (;;) {
    try {
      storage.setItem(key, value);
      return;
    } catch {
      const oldest = others()[0];
      if (!oldest) {
        console.warn("[traceNotes] notes not kept: this browser's storage is full");
        return;
      }
      storage.removeItem(oldest.key);
      held.delete(`${owner}|${oldest.key.slice(owner.length + 1)}`);
    }
  }
}

function change(session, fn) {
  const owner = ownerPrefix();
  // Another tab may have written this session since it was read here.
  if (owner) held.delete(`${owner}|${session}`);
  const cur = entryOf(session, owner);
  const next = fn(cur);
  if (!next) return;
  const entry = cleanEntry({ ...next, at: Date.now() }, session);
  held.set(`${owner ?? ""}|${session}`, entry);
  if (owner) persist(owner, session, entry);
  emit();
}

/** One session's notes, in the order they were pinned. */
export function traceNotesOf(session) {
  const s = lower(session);
  return s ? entryOf(s).notes : NONE;
}

/**
 * Pin notes on sessions: each note carries its `session`. Notes already held
 * are left as they are; returns the notes added.
 */
export function addTraceNotes(notes) {
  const bySession = new Map();
  for (const n of notes || []) {
    const s = lower(n?.session);
    if (!s || !n?.id) continue;
    if (!bySession.has(s)) bySession.set(s, []);
    bySession.get(s).push(n);
  }
  const added = [];
  for (const [session, list] of bySession) {
    const cur = entryOf(session);
    const skip = new Set(cur.notes.map((n) => n.id));
    const fresh = list.map((n) => cleanNote(n, session)).filter((n) => n && !skip.has(n.id) && (skip.add(n.id), true));
    if (!fresh.length) continue;
    change(session, (c) => {
      const have = new Set(c.notes.map((n) => n.id));
      const now = fresh.filter((n) => !have.has(n.id));
      if (!now.length) return null;
      added.push(...now);
      return { ...c, notes: [...c.notes, ...now] };
    });
  }
  return added;
}

/**
 * Takes notes off sessions: those whose id starts with `prefix` (a writer
 * replacing what it pinned before). Returns how many went.
 */
export function removeTraceNotes(sessions, prefix) {
  const head = String(prefix || "");
  if (!head) return 0;
  let gone = 0;
  for (const s of new Set((sessions || []).map(lower).filter(Boolean))) {
    if (!entryOf(s).notes.some((n) => n.id.startsWith(head))) continue;
    change(s, (c) => {
      const kept = c.notes.filter((n) => !n.id.startsWith(head));
      if (kept.length === c.notes.length) return null;
      gone += c.notes.length - kept.length;
      return { ...c, notes: kept };
    });
  }
  return gone;
}

/** Forgets every session's notes this reader keeps here, for the chat-data wipe. */
export function clearStoredTraceNotes(tenantId = null) {
  const owner = browserStorageKey(PREFIX, tenantId);
  const storage = globalThis.localStorage;
  if (owner && storage) for (const s of keptSessions(storage, owner)) storage.removeItem(s.key);
  held.clear();
  emit();
}

function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

// A new reader keeps notes of their own; another tab's writes are read again.
onAuthIdentityChange(() => { held.clear(); emit(); });
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key == null || e.key.startsWith(PREFIX)) {
      held.clear();
      emit();
    }
  });
}

/**
 * The notes on these sessions (one id, or several), in one list; the same
 * list until one of them changes.
 */
export function useTraceNotes(sessions) {
  const key = (Array.isArray(sessions) ? sessions : [sessions]).map(lower).filter(Boolean).join(",");
  const v = useSyncExternalStore(subscribe, () => version, () => version);
  const last = useRef(NONE);
  return useMemo(() => {
    const out = key ? key.split(",").flatMap((s) => traceNotesOf(s)) : [];
    const prev = last.current;
    if (out.length === prev.length && out.every((n, i) => n === prev[i])) return prev;
    last.current = out.length ? out : NONE;
    return last.current;
  }, [key, v]);
}
